import { ADVERTISED_STAGES } from "@hakili/domain-types";
import { formatMoney, formatPlate, formatQuantity, pl, t } from "@hakili/i18n";
import { Button, Chip, METADONNEE, TEXTE_SECONDAIRE, initials } from "@hakili/ui";
import { useState } from "react";

import type { InventoryVehicle } from "../session/useSession.js";
import { Camera, Car } from "lucide-react";
import { chipLabel, chipTone, EXITED_STAGES } from "./stages.js";

/**
 * `round F2`, `F2.3`, then DEALER V2 `A2` (D2 `views1.js` `carRows`): **the vehicle row of « Mes véhicules »**, a table row carrying what the card carried.
 *
 * `F3` of the last round built the card's skeleton and the founder's walk found
 * it unlike the drawing. What `F2.2`'s doors made possible, and what this adds:
 *
 * | the drawing (`hakili-dealer.jsx:1341-1436`) | now |
 * |---|---|
 * | the photograph, with a camera badge counting the gallery | the real thumbnail, or the drawing's grey block with its car icon |
 * | the expertise badge UNDER the photo | unchanged from `F3` |
 * | « en ligne depuis N jours » | derived from `asOf`, the server's own instant (`OD-17` as ruled) |
 * | the gesture, when one gesture is obvious | « Mettre en ligne » for an unpublished car, and nothing otherwise |
 *
 * ## The age is a DERIVED fact now, and that is `OD-17`'s whole point
 *
 * `F3` could not render it: no front application owns a clock, `D2` stays as
 * written, and reading the browser's would have been the ruling undone in one
 * line. The instant rides the door instead , `GET me/vehicles` answers `asOf`
 * , so the number is the difference between two facts the server sent. On a
 * dealership's laptop with a wrong clock, the age is still right.
 */

/**
 * The stages where an advert's age still means something (`:677`).
 *
 * `FX-1b`, `M1` (`F5`): **bound to the ONE definition**, `02` §2.4.4's advertised stages in
 * `@hakili/domain-types`. It was typed here and typed again , differently , in the buyer's
 * search index, which selected `stage = 'enligne'` alone: a published car Hakili moved to
 * `expertise` was advertised on this card and absent from the buyer's search. `F5`'s binding is
 * « never a second stage list », and this was the second one.
 */
const ADVERTISED: ReadonlySet<string> = new Set(ADVERTISED_STAGES);

/**
 * Whole days between two dates the SERVER gave us.
 *
 * Both sides are the server's: `listedOn` is a column and `asOf` is the door's
 * own instant. Nothing here reads a clock, and `""` for either yields `null`,
 * which renders no age at all , the honest failure when a door answers oddly.
 */
/**
 * `round F3`, `W4`: **the age counts from PUBLICATION.**
 *
 * It counted from `listed_on`, which is the day the row was written. Those are
 * the same day for a car published as it is listed and a different day for
 * every other , and since `round F2` a car may sit unpublished for as long as
 * its owner likes. A card that read « en ligne depuis 14 jours » for an advert
 * nobody could see was stating, precisely, the age of a database row.
 *
 * Both arguments are still parsed as dates rather than differenced as
 * instants: a day boundary in Bamako is what a person means by « depuis
 * combien de jours », not a count of 86 400-second intervals.
 */
export function daysListed(listedOn: string, asOf: string): number | null {
  const listed = /^(\d{4})-(\d{2})-(\d{2})/u.exec(listedOn);
  const now = /^(\d{4})-(\d{2})-(\d{2})/u.exec(asOf);
  if (listed === null || now === null) return null;
  const from = Date.UTC(Number(listed[1]), Number(listed[2]) - 1, Number(listed[3]));
  const to = Date.UTC(Number(now[1]), Number(now[2]) - 1, Number(now[3]));
  return Math.max((to - from) / 86_400_000, 0);
}

/**
 * `A2`, the designer's review of 07 at 960: **a model year never ends a line alone** (« Toyota Fortuner / 2019 »). A title that ends
 * in a year joins it to the word before with a no-break space; any other title is untouched.
 */
export function keepYearTogether(title: string): string {
  return title.replace(/ (\d{4})$/u, "\u00a0$1");
}

export interface VehicleRowProps {
  readonly vehicle: InventoryVehicle;
  readonly onOpen: () => void;
  /** The server's instant, from the list door. Never a clock. */
  readonly asOf: string;
  /** The cover photograph's URL, when the car has one. */
  readonly coverUrl?: string | undefined;
  /** « Mettre en ligne », offered only when it is the one obvious gesture. */
  readonly onPublish?: (() => void) | undefined;
}

/**
 * `PRICE-GAP-DEALER`, M4 (V-005): **what report stands on the car, in the grid's own words.** The chip read
 * `expertiseReportId`, a link no code writes, so every car said « Sans rapport » while its control card said the
 * cabinet's report had arrived. The door now sends the verdict of the report the car's CURRENT file reads.
 */
export function reportChip(vehicle: {
  readonly reportVerdict?: string | null | undefined;
  readonly expertiseReportId: string | null;
}): { readonly label: string; readonly tone: string; readonly empty: boolean } {
  const verdict = vehicle.reportVerdict ?? null;
  if (verdict === "conforme") {
    return {
      label: t("dealer.vehicules.rapportConforme"),
      tone: "bg-emerald-50 text-emerald-700",
      empty: false,
    };
  }
  if (verdict === "ecartprix") {
    return {
      label: t("dealer.vehicules.rapportEcartPrix"),
      tone: "bg-amber-50 text-amber-700",
      empty: false,
    };
  }
  if (verdict === "nonconforme") {
    return {
      label: t("dealer.vehicules.rapportNonConforme"),
      tone: "bg-rose-50 text-rose-700",
      empty: false,
    };
  }
  return vehicle.expertiseReportId === null
    ? { label: t("dealer.vehicules.sansRapport"), tone: "bg-gray-100 text-gray-500", empty: true }
    : { label: t("dealer.vehicules.avecRapport"), tone: "bg-indigo-50 text-indigo-700", empty: false };
}

/**
 * `#888` (DESIGNER R, change 2): metadata text wears the kit's metadata colour, `#46536F` (7,5:1 on white), declared once in `dealer-colours.css`.
 * The kit's tertiary grey (`gray-400`, 2,5:1) is a decorative mark's, not a sentence's.
 */
export const META_TEXT = "text-[color:var(--hk-text-2)]";

/**
 * `#888` (DESIGNER R, change 4): « Rapport » holds REPORT facts. A price gap is a fact about the price (the card keeps its chip, it has no such header), so the table reads it beside the price and
 * says here only that a report stands.
 */
const tableReportChip = (vehicle: InventoryVehicle): ReturnType<typeof reportChip> =>
  vehicle.reportVerdict === "ecartprix"
    ? // « Rapport rendu » is read from the served `expertiseReportId` (non-null on the row), never from the verdict.
      vehicle.expertiseReportId === null
      ? { label: t("dealer.vehicules.sansRapport"), tone: "bg-gray-100 text-gray-500", empty: true }
      : { label: t("dealer.vehicules.avecRapport"), tone: "bg-indigo-50 text-indigo-700", empty: false }
    : reportChip(vehicle);

export function VehicleRow({
  vehicle,
  onOpen,
  asOf,
  coverUrl,
  onPublish,
}: VehicleRowProps): React.JSX.Element {
  /** Whether the cover's URL answered with something the browser could decode. */
  const [broken, setBroken] = useState(false);
  const report = tableReportChip(vehicle);

  /**
   * The age of the advert, and the two conditions on it (`W4`): a car must be at an advertised stage AND published.
   */
  const days =
    ADVERTISED.has(vehicle.stage) && vehicle.publishedAt !== null
      ? daysListed(vehicle.publishedAt, asOf)
      : null;
  /**
   * `A2`, D2 `carRows`: **« en stock depuis N jours »**, the age of the car in the stock, from `listedOn` and the server's own instant. A car listed today says nothing rather than « 0 jour » (the
   * drawing's own retrait: a zero is not written on every line).
   */
  const inStock = daysListed(vehicle.listedOn, asOf);
  return (
    <tr
      className="cursor-pointer border-t border-gray-200 align-middle transition-colors hover:bg-gray-50"
      data-testid={`vehicule-carte-${vehicle.stockReference}`}
      // D2 `data-act="opencar"`: the WHOLE row opens the car; the button below stays the keyboard's way in.
      onClick={onOpen}
    >
      <td className="w-full px-4 py-3">
        <button type="button" className="flex w-full items-center gap-3 text-left">
          <div
            className="relative hidden h-14 w-20 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gray-100 lg:flex"
            data-testid={`vehicule-photo-${vehicle.stockReference}`}
          >
            {coverUrl === undefined || broken ? (
              // The drawing's own placeholder: the grey block with a car in it, never an empty frame.
              <Car className="h-6 w-6 text-gray-300" aria-hidden="true" />
            ) : (
              <img
                src={coverUrl}
                alt=""
                className="h-full w-full object-cover object-center"
                onError={() => {
                  // `round F3`: a photograph that will not load shows the drawing's grey block, never a broken frame.
                  setBroken(true);
                }}
              />
            )}
            <span
              className={`absolute right-1 bottom-1 flex items-center gap-1 rounded-full bg-white px-1.5 py-0.5 text-[10px] font-medium tabular-nums ${TEXTE_SECONDAIRE}`}
              data-testid={`vehicule-photos-${vehicle.stockReference}`}
            >
              <Camera className="h-2.5 w-2.5 text-gray-400" aria-hidden="true" />
              {String(vehicle.photoCount)}
            </span>
          </div>
          <div className="min-w-0">
            {/* `R67`: the title WRAPS. « Mitsubishi ASX 2... » names no car. */}
            <p
              className="text-sm leading-snug font-semibold text-gray-900"
              data-testid={`vehicule-titre-${vehicle.stockReference}`}
            >
              {keepYearTogether(vehicle.title)}
            </p>
            {/* The reference AND the plate, as the record spells it (`R69`), the plate only when the car has one (`round F4`). */}
            <p className={`tabular-nums ${METADONNEE} ${META_TEXT}`}>
              {vehicle.stockReference}
              {vehicle.plate === null || vehicle.plate === "" ? null : ` · ${formatPlate(vehicle.plate)}`}
            </p>
            {/* NOT `truncate` (`R67`): « Aucun contact » is whole. `R65`: a count, not money. */}
            <p className={`tabular-nums ${METADONNEE} ${TEXTE_SECONDAIRE}`}>
              {formatQuantity(vehicle.mileageKm)} km
              {/* D-1030-3, item 3 (WF-DLR-02 step 2): « Aucun contact » is an EMPTY cell, still announced to screen readers; a buyer is written. */}
              {vehicle.hasBuyer ? " · " : null}
              <span
                className={vehicle.hasBuyer ? "" : "sr-only"}
                data-testid={`vehicule-contact-${vehicle.stockReference}`}
              >
                {vehicle.hasBuyer ? t("dealer.vehicules.acheteur") : t("dealer.vehicules.aucunContact")}
              </span>
            </p>
            {inStock === null || inStock < 1 ? null : (
              <p
                className={`tabular-nums ${METADONNEE} ${TEXTE_SECONDAIRE}`}
                data-testid={`vehicule-stock-${vehicle.stockReference}`}
              >
                {t("dealer.vehicules.enStockDepuis")} {pl(inStock, t("dealer.vehicules.jour"))}
              </p>
            )}
          </div>
        </button>
      </td>

      <td className="px-4 py-3 text-right whitespace-nowrap">
        <p className="inline-flex items-center gap-2 text-sm font-semibold tabular-nums">
          {/* `round F6`, `OD-15`: who follows this car, in two letters; absent is silent. */}
          {(vehicle.assigneeName ?? null) === null ? null : (
            <span
              title={`${t("dealer.vehicules.suiviPar")} ${vehicle.assigneeName ?? ""}`}
              className="flex h-5 w-5 items-center justify-center rounded-full bg-indigo-100 text-[10px] font-semibold text-indigo-700"
              data-testid={`vehicule-suivi-${vehicle.stockReference}`}
            >
              {initials(vehicle.assigneeName ?? "")}
            </span>
          )}
          {formatMoney(vehicle.priceAmount)}
        </p>
        {/* `P0-DLW-032`: at most ONE coloured band, derived from the car, never typed; never on a terminal branch. */}
        {typeof vehicle.priceDrop !== "number" ||
        vehicle.priceDrop <= 0 ||
        EXITED_STAGES.has(vehicle.stage) ? null : (
          <p
            className="mt-1 text-xs font-medium text-emerald-700 tabular-nums"
            data-testid={`vehicule-baisse-${vehicle.stockReference}`}
          >
            {t("dealer.vehicules.prixBaisse")} {formatMoney(vehicle.priceDrop)}
          </p>
        )}
        {/* `#888` change 4: the price gap is read next to the price, not under « Rapport ». */}
        {vehicle.reportVerdict === "ecartprix" ? (
          <p className="mt-1">
            <span
              className="inline-block rounded-md bg-amber-50 px-2 py-1 text-xs leading-tight font-medium text-amber-700"
              data-testid={`vehicule-ecart-prix-${vehicle.stockReference}`}
            >
              {t("dealer.vehicules.rapportEcartPrix")}
            </span>
          </p>
        ) : null}
      </td>

      <td className="px-4 py-3 whitespace-nowrap">
        <div className="flex flex-wrap items-center gap-2">
          {/* `round F3`, `W4`: « En ligne » is a claim about a BUYER, so an unpublished car does not make it. */}
          {vehicle.published ? (
            <Chip tone={chipTone(vehicle)}>{chipLabel(vehicle, true)}</Chip>
          ) : ADVERTISED.has(vehicle.stage) ? (
            <Chip tone="neutre">{t("dealer.vehicules.nonPublie")}</Chip>
          ) : (
            <>
              <Chip tone={chipTone(vehicle)}>{chipLabel(vehicle, true)}</Chip>
              <Chip tone="neutre">{t("dealer.vehicules.nonPublie")}</Chip>
            </>
          )}
          {days === null || days < 1 ? null : (
            <span
              className={`tabular-nums ${METADONNEE} ${META_TEXT}`}
              data-testid={`vehicule-anciennete-${vehicle.stockReference}`}
            >
              {t("dealer.vehicules.depuis")} {pl(days, t("dealer.vehicules.jour"))}
            </span>
          )}
          {/* `#888` change 4: what the card says, the table says (H keeps « Photo requise » in the state column). */}
          {vehicle.photoCount === 0 ? (
            <span
              className="inline-block rounded-md bg-amber-50 px-2 py-1 text-xs leading-tight font-medium text-amber-700"
              data-testid={`vehicule-photo-requise-${vehicle.stockReference}`}
            >
              {t("dealer.vehicules.photoRequise")}
            </span>
          ) : null}
        </div>
      </td>

      <td className="px-4 py-3 whitespace-nowrap">
        {/* D-1030-3, item 3: no report is an EMPTY cell (« Sans rapport » for screen readers only); a report is written. */}
        <span
          className={
            report.empty
              ? "sr-only"
              : `inline-block rounded-md px-2 py-1 text-xs font-medium leading-tight ${report.tone}`
          }
          data-testid={`vehicule-expertise-${vehicle.stockReference}`}
        >
          {report.label}
        </span>
      </td>

      {/* Publishing is not opening: the cell stops the row's own click. */}
      <td
        className="px-4 py-3 text-right"
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        {/* The gesture on the row (`R76`), and ONLY where there is one gesture with nothing to arbitrate: an unpublished car has exactly one, put it online. `#888`: the kit's SECONDARY, one primary per screen. */}
        {onPublish === undefined ? null : (
          <Button
            variant="secondary"
            size="sm"
            onClick={onPublish}
            className="!rounded-lg !px-3 !py-1.5 text-xs whitespace-nowrap"
            data-testid={`vehicule-publier-${vehicle.stockReference}`}
          >
            {t("dealer.vehicules.publier")}
          </Button>
        )}
      </td>
    </tr>
  );
}
